import { useEffect, useRef, useState, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { api } from '@/api/client'
import { useSettings } from '@/api/hooks'
import { searchNotes, searchPatients } from '@/api/search'
import { useFeedbackStore } from '@/features/feedback/feedbackStore'
import { useAuthStore } from '@/stores/auth'
import { useRecordingStore } from '@/stores/recording'
import { useRecordingPrefs } from '@/stores/recordingPrefs'
import { cn } from '@/utils/cn'
import { recordSelection, getTopEntries, sortByFrecency } from '@/utils/frecency'
import { useCommands, type Command } from '@/utils/commandRegistry'
import {
  Search,
  FileText,
  Users,
  Calendar,
  Settings,
  Shield,
  ArrowRight,
  LayoutDashboard,
  Tag,
  UserPlus,
  Building2,
  Pill,
  Stethoscope,
  BarChart3,
  MessageSquare,
  DatabaseZap,
  AlertTriangle,
  Mic,
  Plus,
  Clock,
  User,
  Sparkles,
  X,
  CircleHelp,
  Compass,
  MessageSquarePlus,
} from 'lucide-react'
import { useAssistResolve } from './useAssistResolve'
import { useHelpPanelStore } from '@/stores/helpPanel'
import { helpArticlePath, matchHelpIndex, mergeHelpMatches, useHelpFullText, useHelpIndex } from '@/components/help/helpApi'
import { assistShown, type AssistRow } from './assistResults'
import type { Patient, Note, NoteListResponse, PatientListResponse, CommandPaletteResponse, CommandPaletteResult } from '@/api/types'
import type { LucideIcon } from 'lucide-react'
import { formatDob } from '@/utils/dateFormat'
import { patientContactParts } from '@/utils/patientContact'

// --- Fuzzy string matching ---
// Damerau-Levenshtein distance handles insertions, deletions, substitutions,
// AND transpositions (the most common typo type: "appoinmtent" → "appointment").
function fuzzyScore(query: string, target: string): number {
  const q = query.toLowerCase()
  const t = target.toLowerCase()

  // Exact substring is best
  if (t.includes(q)) return 100

  const qLen = q.length
  const tLen = t.length

  // Skip if lengths are too different (more than 40% difference)
  if (Math.abs(qLen - tLen) > Math.max(qLen, tLen) * 0.4) return 0

  // Damerau-Levenshtein distance matrix
  const d: number[][] = Array.from({ length: qLen + 1 }, () => Array(tLen + 1).fill(0))
  for (let i = 0; i <= qLen; i++) d[i][0] = i
  for (let j = 0; j <= tLen; j++) d[0][j] = j

  for (let i = 1; i <= qLen; i++) {
    for (let j = 1; j <= tLen; j++) {
      const cost = q[i - 1] === t[j - 1] ? 0 : 1
      d[i][j] = Math.min(
        d[i - 1][j] + 1,       // deletion
        d[i][j - 1] + 1,       // insertion
        d[i - 1][j - 1] + cost  // substitution
      )
      // Transposition
      if (i > 1 && j > 1 && q[i - 1] === t[j - 2] && q[i - 2] === t[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + cost)
      }
    }
  }

  const distance = d[qLen][tLen]
  const maxLen = Math.max(qLen, tLen)

  // Allow up to ~30% edit distance
  if (distance > maxLen * 0.3) return 0

  return Math.round((1 - distance / maxLen) * 80)
}

function fuzzyMatch(query: string, ...targets: (string | undefined)[]): boolean {
  return targets.some((t) => t != null && fuzzyScore(query, t) > 0)
}

// --- AI trigger detection ---
// Only invoke the AI endpoint when the query contains signal words indicating
// contextual, temporal, relational, or intent-based resolution is needed.
// Simple searches and navigation stay on the fast path (no AI call).
const AI_TRIGGERS = /\b(last|next|my|their|today|tomorrow|yesterday|this\s+week|this\s+month|this\s+patient|current|recent|pending|expiring|low\s+stock|who'?s|show\s+me|find|go\s+to|how\s+many|how\s+much|from\s+(mon|tue|wed|thu|fri|sat|sun|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec))\b|'s\s/i

/**
 * How long ⌘K remembers where you were (CLA-1838). Reopened within this, it
 * comes back with the last query and its results, so "Read more" on a help
 * answer, then ⌘K again, returns to the answers. After it, ⌘K opens fresh.
 */
export const PALETTE_MEMORY_MS = 5 * 60 * 1000

// Map backend icon string names to LucideIcon components
const iconMap: Record<string, LucideIcon> = {
  'file-text': FileText,
  'user': User,
  'users': Users,
  'calendar': Calendar,
  'settings': Settings,
  'pill': Pill,
  'tag': Tag,
  'shield': Shield,
  'message-square': MessageSquare,
  'bar-chart': BarChart3,
  'alert-triangle': AlertTriangle,
  'user-plus': UserPlus,
  'package': Building2,
  'shopping-cart': LayoutDashboard,
  'stethoscope': Stethoscope,
  'inbox': DatabaseZap,
}

function getCommandContext() {
  const { activeClinicId } = useAuthStore.getState()
  const pathname = window.location.pathname
  const patientMatch = pathname.match(/^\/patients\/([a-f0-9-]+)$/)
  return {
    current_route: pathname,
    current_patient_uuid: patientMatch?.[1] ?? null,
    active_clinic_id: activeClinicId,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }
}

// Navigation items are now registered via commandRegistry (see AppShell.tsx).
// The palette consumes them via useCommands().

// --- Result types ---

interface ResultItem {
  id: string
  type: 'action' | 'navigation' | 'patient' | 'note' | 'recent' | 'appointment' | 'assist'
  aiEnhanced?: boolean
  /** A "Search or ask" row (CLA-1813); help rows render as an answer card. */
  assist?: AssistRow
  /** Help cards: "Read more" opens the quick view (the primary action is "Take me there" when there is one). */
  readMore?: () => void
  shortcut?: string
  label: string
  subtitle?: string
  detail?: string
  icon: LucideIcon
  action: () => void
  /** Entity UUID for Tab-to-narrow (e.g. patient UUID) */
  entityId?: string
}

interface SubcommandLevel {
  type: 'patient'
  entityId: string
  entityLabel: string
  placeholder: string
}

interface ResultGroup {
  type: string
  label: string
  items: ResultItem[]
  /** A line under the group label ("Nothing found for …"). */
  note?: string
}

// --- Component ---

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [patients, setPatients] = useState<Patient[]>([])
  const [notes, setNotes] = useState<Note[]>([])
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const hasPermission = useAuthStore((s) => s.hasPermission)
  const recording = useRecordingStore()
  const registeredCommands = useCommands(hasPermission)
  // Includes both nav destinations and one-shot actions (e.g. "Send
  // feedback", "Report an incident"). They flow through the same
  // filter/render path; the var name is historical.
  const navCommands = registeredCommands.filter(
    (c) => c.category === 'navigation' || c.category === 'action',
  )

  // Debounce timer ref
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // AI intent resolution state
  const [aiResults, setAiResults] = useState<CommandPaletteResult[]>([])
  const [aiInterpretation, setAiInterpretation] = useState<string | null>(null)
  const [aiLoading, setAiLoading] = useState(false)
  const aiTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const aiAbortRef = useRef<AbortController | undefined>(undefined)

  // Subcommand stack (Tab to narrow into a patient)
  const [stack, setStack] = useState<SubcommandLevel[]>([])
  const scopedPatientUuid = stack.length > 0 && stack[stack.length - 1].type === 'patient'
    ? stack[stack.length - 1].entityId
    : null

  // Proactive suggestions (fetched on open)
  const [suggestions, setSuggestions] = useState<CommandPaletteResult[]>([])

  // "Search or ask" (CLA-1813). When `GET /settings` says `assist_available`,
  // the resolver (`/assist/resolve`) answers in its own section below the
  // live search, and the old `/command-palette/resolve` classifier is not
  // called. Otherwise everything works as before.
  const { data: settings } = useSettings()
  const assistEnabled = settings?.assist_available === true
  const location = useLocation()
  const pagePatientRef = location.pathname.match(/^\/patients\/([a-f0-9-]+)$/)?.[1] ?? null
  const pageNoteRef = new URLSearchParams(location.search).get('note')
  const assist = useAssistResolve(open && assistEnabled, query, {
    patientRef: scopedPatientUuid ?? pagePatientRef,
    noteRef: pageNoteRef,
  })
  const resetAssist = assist.reset
  // Without "Search or ask", help articles come from the index, matched here
  // in the browser: typing "templates" offers Note profiles (CLA-1838). Once
  // typing pauses, the server's full-text matches on article bodies are added
  // after them (the query in a POST body, never the URL).
  const helpIndex = useHelpIndex(open && !assistEnabled)
  const helpFullText = useHelpFullText(query, { enabled: open && !assistEnabled, limit: 3 })
  // Until the user moves the selection, a confident resolver answer is the
  // default Enter target even though it is listed after the live search.
  const [selectionTouched, setSelectionTouched] = useState(false)

  // --- Open/close ---

  // When the palette last closed, and with what typed: reopening soon after
  // keeps everything as it was (PALETTE_MEMORY_MS).
  const lastClosedAtRef = useRef<number | null>(null)
  const resumedRef = useRef(false)
  const queryRef = useRef(query)
  useEffect(() => {
    queryRef.current = query
  }, [query])

  const openPalette = useCallback(() => {
    setOpen(true)
    const closedAt = lastClosedAtRef.current
    resumedRef.current =
      closedAt !== null && queryRef.current.trim() !== '' && Date.now() - closedAt < PALETTE_MEMORY_MS
    if (resumedRef.current) return
    setQuery('')
    setSelectedIndex(0)
    setSelectionTouched(false)
    resetAssist()
    setPatients([])
    setNotes([])
    setAiResults([])
    setAiInterpretation(null)
    setAiLoading(false)
    setStack([])
    setSuggestions([])

    // Fetch proactive suggestions (non-blocking)
    const ctx = getCommandContext()
    api.get<{ suggestions: CommandPaletteResult[] }>(
      `/command-palette/suggestions?timezone=${encodeURIComponent(ctx.timezone)}` +
      (ctx.current_patient_uuid ? `&patient_uuid=${ctx.current_patient_uuid}` : '')
    ).then((res) => {
      setSuggestions(res.suggestions)
    }).catch(() => {})
  }, [resetAssist])

  const closePalette = useCallback(() => {
    lastClosedAtRef.current = Date.now()
    setOpen(false)
  }, [])

  // Global Cmd+K / Ctrl+K listener
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        if (open) {
          closePalette()
        } else {
          openPalette()
        }
      }
      if (e.key === 'Escape' && open) {
        e.preventDefault()
        closePalette()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, openPalette, closePalette])

  // Auto-focus input when opened
  useEffect(() => {
    if (open) {
      // Small delay to let the DOM render
      requestAnimationFrame(() => {
        inputRef.current?.focus()
        // A remembered query is selected, so typing something new replaces it.
        if (resumedRef.current) inputRef.current?.select()
      })
    }
  }, [open])

  // --- Search ---

  // Filter navigation items (fuzzy matching for typo tolerance)
  const filteredNav = query.trim()
    ? navCommands.filter((cmd) => {
        const q = query.trim()
        return fuzzyMatch(q, cmd.label, cmd.subtitle, ...(cmd.keywords ?? []))
      })
    : navCommands.filter((cmd) => cmd.showInDefault)

  // Fetch patients and notes on query change (debounced)
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)

    if (!query.trim()) {
      setPatients([])
      setNotes([])
      setLoading(false)
      return
    }

    setLoading(true)
    timerRef.current = setTimeout(async () => {
      try {
        if (scopedPatientUuid) {
          // Scoped search: only notes and appointments for this patient
          // CLA-1694: what was typed goes in the body, never the URL.
          const noteRes = await searchNotes<NoteListResponse>({
            q: query,
            limit: 5,
            patient_uuid: scopedPatientUuid,
          })
          setPatients([])
          setNotes(noteRes.notes)
        } else {
          const [patientRes, noteRes] = await Promise.all([
            searchPatients<PatientListResponse>({ q: query, limit: 5 }),
            searchNotes<NoteListResponse>({ q: query, limit: 5 }),
          ])
          setPatients(patientRes.patients)
          setNotes(noteRes.notes)
        }
      } catch {
        // Silently ignore search errors
      } finally {
        setLoading(false)
      }
    }, 200)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, scopedPatientUuid])

  // AI intent resolution — fires when trigger signals detected (400ms debounce)
  // Also fires as fallback when fuzzy search returns no results (typo tolerance)
  useEffect(() => {
    if (aiTimerRef.current) clearTimeout(aiTimerRef.current)
    if (aiAbortRef.current) aiAbortRef.current.abort()

    const q = query.trim()
    const hasTrigger = q && AI_TRIGGERS.test(q)
    const fuzzyEmpty = q && !loading && patients.length === 0 && notes.length === 0 && filteredNav.length === 0
    // With "Search or ask" on, the resolver answers instead (CLA-1813).
    if (assistEnabled || (!hasTrigger && !fuzzyEmpty)) {
      setAiResults([])
      setAiInterpretation(null)
      setAiLoading(false)
      return
    }

    setAiLoading(true)
    const controller = new AbortController()
    aiAbortRef.current = controller

    aiTimerRef.current = setTimeout(async () => {
      try {
        const res = await api.post<CommandPaletteResponse>(
          '/command-palette/resolve',
          { query: q, context: getCommandContext() },
        )
        if (!controller.signal.aborted) {
          if (!res.fallback && res.results.length > 0) {
            setAiResults(res.results)
            setAiInterpretation(res.interpretation ?? null)
          } else {
            setAiResults([])
            setAiInterpretation(null)
          }
        }
      } catch {
        if (!controller.signal.aborted) {
          setAiResults([])
          setAiInterpretation(null)
        }
      } finally {
        if (!controller.signal.aborted) setAiLoading(false)
      }
    }, 400)

    return () => {
      if (aiTimerRef.current) clearTimeout(aiTimerRef.current)
      controller.abort()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, loading, patients.length, notes.length, filteredNav.length, assistEnabled])

  // --- Build flat results list ---

  const groups: ResultGroup[] = []
  const q = query.trim()
  const isIdle = recording.state === 'idle'

  // Smart suppression: hide irrelevant fuzzy groups when AI returned actual results
  // Only suppress when AI came back with results — empty AI results means show everything
  const hasAiResults = aiResults.length > 0
  const aiResultTypes = hasAiResults ? new Set(aiResults.map((r) => r.type)) : new Set<string>()
  const suppressPatients =
    hasAiResults &&
    !aiResultTypes.has('patient') &&
    (aiResultTypes.has('note') || aiResultTypes.has('appointment') ||
     aiResultTypes.has('schedule') || aiResultTypes.has('navigation'))
  const suppressNotes =
    hasAiResults &&
    !aiResultTypes.has('note') &&
    (aiResultTypes.has('patient') || aiResultTypes.has('navigation'))

  // Context detection for empty state
  const isRecording = recording.state === 'recording' || recording.state === 'paused'

  // Quick actions — context-aware
  if (!q && scopedPatientUuid) {
    // Scoped to a patient — show patient-specific actions
    const patientLabel = stack[stack.length - 1].entityLabel
    const actions: ResultItem[] = []
    if (isIdle) {
      actions.push({
        id: 'action-record-patient',
        type: 'action',
        label: `Start recording for ${patientLabel}`,
        icon: Mic,
        action: () => {
          recordSelection('action::record')
          closePalette()
          const profileId = useRecordingPrefs.getState().effectiveProfileId() ?? undefined
          recording.startRecording({ profileId })
        },
      })
    }
    actions.push({
      id: 'action-view-patient',
      type: 'action',
      label: `View ${patientLabel}'s record`,
      icon: User,
      action: () => {
        recordSelection(`patient::${scopedPatientUuid}`, { label: patientLabel })
        navigate(`/patients/${scopedPatientUuid}`)
        closePalette()
      },
    })
    groups.push({ type: 'action', label: patientLabel, items: actions })
  } else if (!q) {
    const actions: ResultItem[] = []

    if (isRecording) {
      // During recording: show recording controls
      if (recording.state === 'recording') {
        actions.push({
          id: 'action-pause',
          type: 'action',
          label: 'Pause recording',
          icon: Mic,
          action: () => {
            closePalette()
            recording.pauseRecording()
          },
        })
      } else {
        actions.push({
          id: 'action-resume',
          type: 'action',
          label: 'Resume recording',
          icon: Mic,
          action: () => {
            closePalette()
            recording.resumeRecording()
          },
        })
      }
      actions.push({
        id: 'action-stop',
        type: 'action',
        label: 'Stop recording',
        subtitle: 'Submit for processing',
        icon: Mic,
        action: () => {
          recordSelection('action::stop')
          closePalette()
          recording.stopRecording()
        },
      })
    } else {
      // Normal state: show start recording + create actions
      if (isIdle) {
        actions.push({
          id: 'action-record',
          type: 'action',
          label: 'Start recording',
          subtitle: 'Begin a new consultation',
          icon: Mic,
          action: () => {
            recordSelection('action::record')
            closePalette()
            const profileId = useRecordingPrefs.getState().effectiveProfileId() ?? undefined
            recording.startRecording({ profileId })
          },
        })
      }
      actions.push({
        id: 'action-new-note',
        type: 'action',
        label: 'Create note',
        subtitle: 'New typed note',
        icon: Plus,
        action: () => {
          recordSelection('action::new-note')
          closePalette()
          navigate('/')
          setTimeout(() => document.querySelector<HTMLButtonElement>('[data-new-note]')?.click(), 100)
        },
      })
      actions.push({
        id: 'action-new-patient',
        type: 'action',
        label: 'Create patient',
        icon: UserPlus,
        action: () => {
          recordSelection('action::new-patient')
          closePalette()
          navigate('/patients?new=1')
        },
      })
    }

    groups.push({ type: 'action', label: recording.state === 'paused' ? 'Paused' : isRecording ? 'Listening' : 'Actions', items: actions })

    // Proactive suggestions (from backend — next appointment, draft notes, patient context)
    if (suggestions.length > 0 && !isRecording) {
      groups.push({
        type: 'suggested',
        label: 'Suggested',
        items: suggestions.map((s, i) => {
          const Icon = iconMap[s.icon] || Sparkles
          return {
            id: `suggestion-${i}`,
            type: 'navigation' as const,
            label: s.label,
            subtitle: s.subtitle,
            icon: Icon,
            aiEnhanced: true,
            action: () => {
              navigate(s.path)
              closePalette()
            },
          }
        }),
      })
    }

    // Recent — frecency-sorted mix of patients, nav, notes
    // Suppressed during recording (navigating mid-consultation is disruptive)
    const recentEntries = isRecording ? [] : getTopEntries(8)
    if (recentEntries.length > 0) {
      const recentItems: ResultItem[] = []
      for (const entry of recentEntries) {
        if (!entry.meta) continue
        const [type, id] = entry.id.split('::')
        if (type === 'patient') {
          recentItems.push({
            id: `recent-${id}`,
            type: 'recent' as const,
            label: entry.meta.label,
            subtitle: entry.meta.dob ? formatDob(entry.meta.dob) : entry.meta.subtitle,
            icon: Clock,
            action: () => {
              recordSelection(entry.id, entry.meta)
              navigate(`/patients/${id}`)
              closePalette()
            },
          })
        } else if (type === 'nav') {
          const navCmd = navCommands.find((c) => c.id === id)
          if (navCmd) {
            recentItems.push({
              id: `recent-${id}`,
              type: 'recent' as const,
              label: navCmd.label,
              subtitle: navCmd.subtitle,
              icon: navCmd.icon,
              action: () => {
                recordSelection(entry.id)
                navCmd.action()
                closePalette()
              },
            })
          }
        } else if (type === 'note') {
          recentItems.push({
            id: `recent-${id}`,
            type: 'recent' as const,
            label: entry.meta.label,
            subtitle: entry.meta.subtitle,
            icon: FileText,
            action: () => {
              recordSelection(entry.id, entry.meta)
              navigate(`/?note=${id}`)
              closePalette()
            },
          })
        }
      }
      if (recentItems.length > 0) {
        groups.push({ type: 'recent', label: 'Recent', items: recentItems })
      }
    }
  }

  // AI-resolved results — interleaved into natural groups
  // Build lookup of AI items by their target group type
  const aiItemsByGroup: Record<string, ResultItem[]> = {}
  if (aiResults.length > 0) {
    const fuzzyPatientUuids = new Set(patients.map((p) => p.uuid))
    const fuzzyPaths = new Set(filteredNav.map((c) => c.path).filter(Boolean))

    for (const [i, r] of aiResults.entries()) {
      // Dedup: skip if patient already in fuzzy results or path matches a nav item
      if (r.patient_uuid && fuzzyPatientUuids.has(r.patient_uuid)) continue
      if ((r.type === 'navigation' || r.type === 'schedule') && fuzzyPaths.has(r.path)) continue

      const Icon = iconMap[r.icon] || Sparkles
      const item: ResultItem = {
        id: `ai-${i}-${r.path}`,
        type: (r.type === 'appointment' ? 'appointment' : r.type === 'patient' ? 'patient' : r.type === 'note' ? 'note' : 'navigation') as ResultItem['type'],
        label: r.label,
        subtitle: r.subtitle,
        icon: Icon,
        aiEnhanced: true,
        entityId: r.patient_uuid,
        action: () => {
          if (r.patient_uuid) {
            recordSelection(`patient::${r.patient_uuid}`, { label: r.label })
          }
          navigate(r.path)
          closePalette()
        },
      }

      // Map AI result types to group keys
      const groupKey =
        r.type === 'patient' ? 'patient' :
        r.type === 'note' ? 'note' :
        r.type === 'appointment' ? 'appointment' :
        'navigation'

      if (!aiItemsByGroup[groupKey]) aiItemsByGroup[groupKey] = []
      aiItemsByGroup[groupKey].push(item)
    }

    // Appointments don't have a fuzzy group — add them directly
    if (aiItemsByGroup['appointment']?.length) {
      groups.push({
        type: 'appointment',
        label: 'Appointments',
        items: aiItemsByGroup['appointment'],
      })
    }
  }

  // Navigation — strong matches (prefix on label/keywords) shown early, rest at bottom
  // This ensures "cal" → Calendar appears before notes that happen to contain "cal"
  const navBuildItems = (items: Command[]) => items.map((cmd) => ({
    id: cmd.id,
    type: 'navigation' as const,
    label: cmd.label,
    subtitle: cmd.subtitle,
    icon: cmd.icon,
    shortcut: cmd.shortcut,
    action: () => {
      recordSelection(`nav::${cmd.id}`)
      cmd.action()
      closePalette()
    },
  }))

  const aiNavItems = aiItemsByGroup['navigation'] ?? []
  let weakNavItems: ResultItem[] = []

  // A help or navigation answer from the classifier is shown even when no
  // menu item matches the words typed: "how do I add a passkey?" matches no
  // nav label, and used to end in "No results" with the answer dropped.
  if (q && (filteredNav.length > 0 || aiNavItems.length > 0)) {
    const ql = q.toLowerCase()
    const strongNavMatches = filteredNav.filter((item) =>
      item.label.toLowerCase().startsWith(ql) ||
      item.keywords?.some((kw) => kw.startsWith(ql))
    )
    weakNavItems = navBuildItems(
      filteredNav.filter((item) =>
        !item.label.toLowerCase().startsWith(ql) &&
        !item.keywords?.some((kw) => kw.startsWith(ql))
      )
    )

    // Strong matches go first (before patients/notes)
    if (strongNavMatches.length > 0 || aiNavItems.length > 0) {
      const sortedStrong = sortByFrecency(strongNavMatches, (item) => `nav::${item.id}`)
      groups.push({
        type: 'navigation',
        label: 'Go to',
        items: [...aiNavItems, ...navBuildItems(sortedStrong)],
      })
    }
  }

  // Patients — shown after strong nav matches, frecency-sorted
  const aiPatientItems = aiItemsByGroup['patient'] ?? []
  if ((patients.length > 0 && !suppressPatients) || aiPatientItems.length > 0) {
    const sortedPatients = suppressPatients ? [] : sortByFrecency(patients, (p) => `patient::${p.uuid}`)
    const fuzzyItems = sortedPatients.map((p) => {
        // CLA-1634: the same mobile · email · suburb order as every patient pick list.
        const details = [formatDob(p.dob), ...patientContactParts(p)].filter(Boolean).join(' \u00b7 ')
        const name = `${p.first_name} ${p.last_name}`
        return {
          id: `patient-${p.uuid}`,
          type: 'patient' as const,
          label: name,
          subtitle: details || undefined,
          icon: User,
          entityId: p.uuid,
          action: () => {
            recordSelection(`patient::${p.uuid}`, { label: name, dob: p.dob ?? undefined })
            navigate(`/patients/${p.uuid}`)
            closePalette()
          },
        }
      })
    groups.push({
      type: 'patient',
      label: 'Patients',
      items: [...aiPatientItems, ...fuzzyItems],
    })
  }

  // Notes — frecency-sorted
  const aiNoteItems = aiItemsByGroup['note'] ?? []
  if ((notes.length > 0 && !suppressNotes) || aiNoteItems.length > 0) {
    const sortedNotes = suppressNotes ? [] : sortByFrecency(notes, (n) => `note::${n.uuid}`)
    const fuzzyNoteItems = sortedNotes.map((n) => {
        const label = n.title || n.time_label
        const patientName =
          typeof n.patient?.display_name === 'string' ? n.patient.display_name : undefined
        return {
          id: `note-${n.uuid}`,
          type: 'note' as const,
          label,
          subtitle: patientName,
          detail: n.note?.slice(0, 80)?.replace(/\n/g, ' ') || undefined,
          icon: FileText,
          action: () => {
            recordSelection(`note::${n.uuid}`, { label, subtitle: patientName })
            navigate(`/?note=${n.uuid}`)
            closePalette()
          },
        }
      })
    groups.push({
      type: 'note',
      label: 'Notes',
      items: [...aiNoteItems, ...fuzzyNoteItems],
    })
  }

  // Navigation — empty state shows all default nav items; search state handled above
  if (!q && filteredNav.length > 0) {
    groups.push({
      type: 'navigation',
      label: 'Navigation',
      items: navBuildItems(filteredNav),
    })
  }

  // Help articles whose title, keywords or summary match, then those that
  // mention the words in their text (no "Search or ask"; it answers with help
  // itself). One the classifier already offered is not listed twice.
  if (q && !assistEnabled) {
    const aiPaths = new Set(aiResults.map((r) => r.path))
    const helpItems: ResultItem[] = mergeHelpMatches(matchHelpIndex(helpIndex.data ?? [], q), helpFullText.data, 4)
      .filter((a) => !aiPaths.has(helpArticlePath(a.slug)))
      .map((a) => {
        const row: AssistRow = {
          kind: 'help',
          id: `help-${a.slug}`,
          slug: a.slug,
          title: a.title,
          answer: a.answer || null,
          webPath: null,
        }
        const readMore = () => {
          closePalette()
          useHelpPanelStore.getState().open(a.slug)
        }
        return { id: row.id, type: 'assist' as const, assist: row, label: a.title, icon: CircleHelp, action: readMore, readMore }
      })
    if (helpItems.length > 0) groups.push({ type: 'help', label: 'Help', items: helpItems })
  }

  // Weak nav matches at the bottom (from search split above)
  if (weakNavItems.length > 0) {
    groups.push({ type: 'navigation-more', label: 'Also matches', items: weakNavItems })
  }

  // "Search or ask" (CLA-1813): the resolver's answer, after everything the
  // live search found. While the next answer is on its way the previous one
  // stays up, but only an answer to THIS query can take the default selection.
  let assistDefaultId: string | null = null
  const assistAnswer =
    assistEnabled && q && assist.answer && (assist.answer.query === q || assist.loading)
      ? assist.answer
      : null
  if (assistAnswer) {
    const shown = assistShown(assistAnswer.response, assistAnswer.final, {
      patientRefs: new Set(patients.map((p) => p.uuid)),
      noteRefs: new Set(notes.map((n) => n.uuid)),
      paths: new Set(filteredNav.map((c) => c.path).filter((p): p is string => !!p)),
    })
    const go = (path: string) => {
      navigate(path)
      closePalette()
    }
    const items: ResultItem[] = shown.rows.map((row) => {
      switch (row.kind) {
        case 'help': {
          // The quick view, over the page you are on (CLA-1838).
          const readMore = () => {
            closePalette()
            useHelpPanelStore.getState().open(row.slug)
          }
          const webPath = row.webPath
          return {
            id: row.id,
            type: 'assist' as const,
            assist: row,
            label: row.title,
            icon: CircleHelp,
            // Enter: "Take me there" when the answer has a place, else "Read more".
            action: webPath ? () => go(webPath) : readMore,
            readMore,
          }
        }
        case 'navigate':
          return {
            id: row.id,
            type: 'assist' as const,
            assist: row,
            label: row.title,
            subtitle: row.subtitle ?? undefined,
            icon: Compass,
            action: () => go(row.webPath),
          }
        case 'patient':
          return {
            id: row.id,
            type: 'assist' as const,
            assist: row,
            label: row.name,
            subtitle: row.subtitle ?? undefined,
            icon: User,
            entityId: row.ref,
            action: () => {
              recordSelection(`patient::${row.ref}`, { label: row.name })
              go(`/patients/${row.ref}`)
            },
          }
        case 'note':
          return {
            id: row.id,
            type: 'assist' as const,
            assist: row,
            label: row.title,
            subtitle: row.subtitle ?? undefined,
            icon: FileText,
            action: () => {
              recordSelection(`note::${row.ref}`, { label: row.title, subtitle: row.subtitle ?? undefined })
              go(`/?note=${row.ref}`)
            },
          }
      }
    })

    if (shown.showNothingFound && assistAnswer.query === q && groups.length === 0) {
      groups.push({
        type: 'assist',
        label: 'Ask Claro',
        note: `Nothing found for “${q}”`,
        items: [{
          id: 'assist-feedback',
          type: 'assist',
          label: 'Send feedback',
          subtitle: 'Tell us what you were looking for',
          icon: MessageSquarePlus,
          action: () => {
            closePalette()
            useFeedbackStore.getState().open({ entryPoint: 'cmd_k', prefill: q })
          },
        }],
      })
    } else if (items.length > 0) {
      groups.push({ type: 'assist', label: 'Ask Claro', items })
      if (assistAnswer.query === q) assistDefaultId = shown.defaultRowId
    }
  }

  const flatItems = groups.flatMap((g) => g.items)
  const assistDefaultIndex =
    !selectionTouched && assistDefaultId ? flatItems.findIndex((i) => i.id === assistDefaultId) : -1
  const activeIndex = assistDefaultIndex >= 0 ? assistDefaultIndex : selectedIndex

  // Clamp selected index when results change
  useEffect(() => {
    setSelectedIndex((prev) => Math.min(prev, Math.max(flatItems.length - 1, 0)))
  }, [flatItems.length])

  // --- Keyboard navigation ---

  function onInputKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectionTouched(true)
      setSelectedIndex(Math.max(Math.min(activeIndex + 1, flatItems.length - 1), 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectionTouched(true)
      setSelectedIndex(Math.max(activeIndex - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const item = flatItems[activeIndex]
      if (item) item.action()
      // Nothing to go to yet: ask the resolver now rather than after the pause.
      else if (assistEnabled) assist.submitFinal()
    } else if (e.key === 'Tab' && !e.shiftKey) {
      // Tab to narrow into a patient
      const item = flatItems[activeIndex]
      if (item?.type === 'patient' && item.entityId) {
        e.preventDefault()
        setStack((s) => [...s, {
          type: 'patient',
          entityId: item.entityId!,
          entityLabel: item.label,
          placeholder: `Search ${item.label}'s notes...`,
        }])
        setQuery('')
        setSelectedIndex(0)
      }
    } else if (e.key === 'Backspace' && query === '' && stack.length > 0) {
      // Backspace on empty input pops the stack
      e.preventDefault()
      setStack((s) => s.slice(0, -1))
      setSelectedIndex(0)
    }
  }

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return
    const el = listRef.current.querySelector('[data-selected="true"]')
    if (el) {
      el.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  if (!open) return null

  // --- Render ---

  let itemCounter = -1

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={closePalette}
      />

      {/* Modal */}
      <div className="relative bg-surface rounded-xl shadow-2xl border border-border max-w-lg w-full mx-4 flex flex-col overflow-hidden">
        {/* Search input */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border-subtle">
          <Search className="w-4 h-4 text-ink-faint shrink-0" />
          {stack.map((level, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-claro-50 text-claro-700 rounded-md shrink-0"
            >
              {level.entityLabel}
              <button
                onClick={() => { setStack((s) => s.slice(0, i)); setQuery(''); setSelectedIndex(0) }}
                className="text-claro-400 hover:text-claro-600"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setSelectedIndex(0)
              setSelectionTouched(false)
            }}
            onKeyDown={onInputKeyDown}
            placeholder={stack.length > 0 ? stack[stack.length - 1].placeholder : "Search, or ask anything..."}
            className="flex-1 text-base bg-transparent text-ink placeholder:text-ink-faint focus:outline-none min-w-0"
          />
          {assistEnabled ? (
            assist.loading && query.trim() ? (
              <div
                aria-label="Asking Claro"
                className="w-3.5 h-3.5 border-2 border-claro-200 border-t-claro-600 rounded-full animate-spin shrink-0"
              />
            ) : loading ? (
              <div className="w-4 h-4 border-2 border-claro-200 border-t-claro-600 rounded-full animate-spin shrink-0" />
            ) : null
          ) : aiLoading ? (
            <div className="flex items-center gap-1.5 shrink-0">
              <Sparkles className="w-3.5 h-3.5 text-claro-400 animate-pulse" />
              <div className="w-3.5 h-3.5 border-2 border-claro-200 border-t-claro-600 rounded-full animate-spin" />
            </div>
          ) : loading ? (
            <div className="w-4 h-4 border-2 border-claro-200 border-t-claro-600 rounded-full animate-spin shrink-0" />
          ) : query.trim() && AI_TRIGGERS.test(query.trim()) ? (
            <Sparkles className="w-3.5 h-3.5 text-ink-faint shrink-0" />
          ) : null}
        </div>

        {/* AI interpretation */}
        {aiInterpretation && aiResults.length > 0 && (
          <div className="px-4 py-1.5 border-b border-border-subtle flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-claro-400" />
            <span className="text-xs text-ink-faint">{aiInterpretation}</span>
          </div>
        )}

        {/* Results */}
        <div ref={listRef} className="overflow-y-auto max-h-[400px] py-1">
          {groups.length === 0 && query.trim() && !loading && !aiLoading && !(assistEnabled && assist.loading) && (
            <div className="px-4 py-8 text-center text-sm text-ink-faint">
              No results for "{query}"
            </div>
          )}

          {groups.map((group) => (
            <div key={group.type}>
              <div className="px-4 pt-3 pb-1 flex items-center gap-1.5">
                {group.type === 'appointment' && <Sparkles className="w-3 h-3 text-ink-faint" />}
                <span className="text-[10px] font-medium uppercase tracking-wider text-ink-faint">
                  {group.label}
                </span>
              </div>
              {group.note && (
                <p className="px-4 pb-1 text-sm text-ink-faint">{group.note}</p>
              )}
              {group.items.map((item) => {
                itemCounter++
                const index = itemCounter
                const isSelected = index === activeIndex
                const Icon = item.icon
                const hover = () => {
                  setSelectionTouched(true)
                  setSelectedIndex(index)
                }
                if (item.assist?.kind === 'help') {
                  return (
                    <HelpAnswerRow
                      key={item.id}
                      row={item.assist}
                      selected={isSelected}
                      onPrimary={item.action}
                      onReadMore={item.readMore ?? item.action}
                      onHover={hover}
                    />
                  )
                }
                return (
                  <button
                    key={item.id}
                    data-selected={isSelected}
                    onClick={item.action}
                    onMouseEnter={hover}
                    className={cn(
                      'flex items-center gap-3 w-full px-4 py-2 text-left text-sm transition-colors',
                      isSelected ? 'bg-claro-50' : 'hover:bg-surface-tertiary',
                    )}
                  >
                    <Icon className={cn('w-4 h-4 shrink-0', item.type === 'action' || item.type === 'assist' || item.aiEnhanced ? 'text-claro-600' : 'text-ink-tertiary')} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="text-ink truncate">{item.label}</span>
                        {item.subtitle && (
                          <span className="text-ink-faint text-xs truncate shrink-0">{item.subtitle}</span>
                        )}
                      </div>
                      {item.detail && (
                        <p className="text-xs text-ink-faint truncate mt-0.5">{item.detail}</p>
                      )}
                    </div>
                    {item.shortcut && (
                      <kbd className="text-[10px] text-ink-faint font-mono tracking-wider shrink-0">
                        {item.shortcut}
                      </kbd>
                    )}
                    <ArrowRight className={cn(
                      'w-3.5 h-3.5 text-ink-faint shrink-0 transition-opacity',
                      isSelected ? 'opacity-100' : 'opacity-0',
                    )} />
                  </button>
                )
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-border-subtle flex items-center gap-4 text-[11px] text-ink-faint">
          <span><kbd className="font-mono">↑↓</kbd> navigate</span>
          <span><kbd className="font-mono">Enter</kbd> select</span>
          <span><kbd className="font-mono">Tab</kbd> narrow</span>
          {stack.length > 0 && <span><kbd className="font-mono">⌫</kbd> back</span>}
          <span><kbd className="font-mono">Esc</kbd> close</span>
        </div>
      </div>
    </div>
  )
}

/**
 * A help answer (CLA-1813): the article's title and its short answer, with
 * "Take me there" when the answer has a place in the app and "Read more",
 * which opens the article in the quick view over the current page (CLA-1838).
 * Enter on the row does "Take me there" when there is one, else "Read more".
 */
function HelpAnswerRow({
  row,
  selected,
  onPrimary,
  onReadMore,
  onHover,
}: {
  row: Extract<AssistRow, { kind: 'help' }>
  selected: boolean
  onPrimary: () => void
  onReadMore: () => void
  onHover: () => void
}) {
  return (
    <div
      data-selected={selected}
      data-testid="assist-help-card"
      onMouseEnter={onHover}
      className={cn(
        'mx-2 my-1 rounded-lg border px-3 py-2.5 transition-colors',
        selected ? 'border-claro-200 bg-claro-50' : 'border-border-subtle hover:bg-surface-tertiary',
      )}
    >
      <button type="button" onClick={onPrimary} className="flex w-full items-start gap-3 text-left">
        <CircleHelp className="mt-0.5 h-4 w-4 shrink-0 text-claro-600" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-ink">{row.title}</p>
          {row.answer && <p className="mt-0.5 line-clamp-3 text-sm text-ink-secondary">{row.answer}</p>}
        </div>
      </button>
      <div className="mt-2 flex items-center gap-2 pl-7">
        {row.webPath && (
          <button
            type="button"
            onClick={onPrimary}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-claro-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-claro-700"
          >
            Take me there
            <ArrowRight className="h-3 w-3" />
          </button>
        )}
        <button
          type="button"
          onClick={onReadMore}
          className="shrink-0 whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium text-claro-700 hover:bg-claro-100"
        >
          Read more
        </button>
        {selected && (
          <kbd className="ml-auto hidden truncate font-mono text-[10px] text-ink-faint sm:inline">
            Enter: {row.webPath ? 'take me there' : 'read more'}
          </kbd>
        )}
      </div>
    </div>
  )
}
